import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { theme } from "./theme/muiTheme";
import { AuthProvider } from "./context/AuthContext";
import PrivateGate from "./components/PrivateGate";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Funnel from "./pages/Funnel";
import Results from "./pages/Results";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Booking from "./pages/Booking";
import Appointment from "./pages/Appointment";
import Dashboard from "./pages/Dashboard";
import Courses from "./pages/Courses";
import Course from "./pages/Course";
import SelfTest from "./pages/SelfTest";
import Certificate from "./pages/Certificate";
import Admin from "./pages/Admin";
import ForPsychologists from "./pages/ForPsychologists";
import ProviderTerms from "./pages/ProviderTerms";
import Directory from "./pages/Directory";
import HowItWorks from "./pages/HowItWorks";
import ForOrganisations from "./pages/ForOrganisations";
import { Privacy, Terms, Cookies, About, Contact, SubProcessors, HowWeMatch, PrivacyStandard } from "./pages/info";
import Reimbursement from "./pages/Reimbursement";
import Faq from "./pages/Faq";
import Pricing from "./pages/Pricing";
import Quality from "./pages/Quality";
import BarometerInfo from "./pages/BarometerInfo";
import Theme from "./pages/Theme";
import MyData from "./pages/MyData";
import BarometerRespond from "./pages/BarometerRespond";
import NotFound from "./pages/NotFound";


/** Reset de scrollpositie bij elke route-wissel (SPA-navigatie behoudt die
 *  anders — je landde soms midden op of onderaan de nieuwe pagina).
 *  Hash-links (#anker) behouden hun eigen gedrag. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  // Capture a referral source (e.g. ?source=vindhulp) once, for attribution at intake.
  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get("source");
    if (src) { try { sessionStorage.setItem("carefunded_ref", src); } catch { /* ignore */ } }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Suspense fallback={null}>
          {/* Pre-launch: PrivateGate hides all routes unless the site is public
              or the visitor is a logged-in platform admin. The Router must wrap
              the gate: the gate's login form renders router <Link>s, which
              crash the whole app when mounted outside a <BrowserRouter>. */}
          <BrowserRouter>
          <PrivateGate>
            <ScrollToTop />
            <Routes>
              {/* Content pages get the Header + Footer chrome. */}
              <Route path="/" element={<Layout><Landing /></Layout>} />
              <Route path="/resultaat/:id" element={<Layout><Results /></Layout>} />
              <Route path="/boek/:psychologistId" element={<Layout><Booking /></Layout>} />
              <Route path="/afspraak/:id" element={<Layout><Appointment /></Layout>} />
              <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
              <Route path="/psycholoog-voorwaarden" element={<Layout><ProviderTerms /></Layout>} />
              <Route path="/psychologen" element={<Layout><Directory /></Layout>} />
              <Route path="/psychologen/:region" element={<Layout><Directory /></Layout>} />
              <Route path="/psychologen/:region/:topic" element={<Layout><Directory /></Layout>} />
              <Route path="/zelfhulp" element={<Layout><Courses /></Layout>} />
              <Route path="/zelfhulp/test/:slug" element={<Layout><SelfTest /></Layout>} />
              <Route path="/zelfhulp/:slug/certificaat" element={<Layout><Certificate /></Layout>} />
              <Route path="/zelfhulp/:slug" element={<Layout><Course /></Layout>} />
              <Route path="/admin" element={<Layout><Admin /></Layout>} />
              <Route path="/voor-psychologen" element={<Layout><ForPsychologists /></Layout>} />
              <Route path="/hoe-het-werkt" element={<Layout><HowItWorks /></Layout>} />
              <Route path="/hoe-matchen-wij" element={<Layout><HowWeMatch /></Layout>} />
              <Route path="/voor-organisaties" element={<Layout><ForOrganisations /></Layout>} />
              {/* Publieksvriendelijke alias voor de B2B-pagina. */}
              <Route path="/voor-werkgevers" element={<Layout><ForOrganisations /></Layout>} />
              <Route path="/terugbetaling" element={<Layout><Reimbursement /></Layout>} />
              <Route path="/tarieven" element={<Layout><Pricing /></Layout>} />
              <Route path="/veelgestelde-vragen" element={<Layout><Faq /></Layout>} />
              <Route path="/kwaliteit-en-erkenning" element={<Layout><Quality /></Layout>} />
              <Route path="/welzijnsbarometer" element={<Layout><BarometerInfo /></Layout>} />
              {/* Thema-overzichten (informatief/navigerend, zie content/themes.ts). */}
              <Route path="/thema/:slug" element={<Layout><Theme /></Layout>} />
              <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
              <Route path="/privacystandaard" element={<Layout><PrivacyStandard /></Layout>} />
              <Route path="/voorwaarden" element={<Layout><Terms /></Layout>} />
              <Route path="/algemene-voorwaarden" element={<Layout><Terms /></Layout>} />
              <Route path="/sub-verwerkers" element={<Layout><SubProcessors /></Layout>} />
              <Route path="/cookies" element={<Layout><Cookies /></Layout>} />
              <Route path="/over-ons" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/mijn-gegevens" element={<Layout><MyData /></Layout>} />
              {/* Anonymous org-barometer respond page (shared wave link, no login). */}
              <Route path="/barometer/:token" element={<Layout><BarometerRespond /></Layout>} />
              <Route path="/inloggen" element={<Layout><Login mode="login" /></Layout>} />
              <Route path="/registreren" element={<Layout><Login mode="register" /></Layout>} />
              <Route path="/wachtwoord-vergeten" element={<Layout><ForgotPassword /></Layout>} />
              <Route path="/reset-wachtwoord" element={<Layout><ResetPassword /></Layout>} />
              {/* The funnel uses its own minimal chrome (no global header/footer). */}
              <Route path="/start" element={<Funnel />} />
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </PrivateGate>
          </BrowserRouter>
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  );
}
